import { merge, cloneDeep } from "lodash";
import { EntityError } from "./types";
import { falsyCheckDate } from "utils";
import JsonViewer from "shared/JsonViewer";
import { Box, Chip, useTheme } from "@mui/material";

export enum StateIds {
  EntityError = "entity-error",
}

enum HeadCellIds {
  ID = "id",
  CreateAt = "createdAt",
  EntityId = "entityId",
  ErrorDetail = "ErrorDetail",
  ErrorSource = "ErrorSource",
  ErrorType = "ErrorType",
  TargetServiceId = "TargetServiceId",
}

type FormattedRowData = EntityError;

// Separate component to properly use React hooks
const ErrorDetailCell = ({ errorDetail }: FormattedRowData) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  
  // Parse errorDetail if it's a string
  let parsedDetail: Record<string, any> = {};
  if (typeof errorDetail === 'string') {
    try {
      parsedDetail = JSON.parse(errorDetail);
    } catch {
      // If not valid JSON, keep as string
      parsedDetail = { message: errorDetail };
    }
  } else {
    parsedDetail = errorDetail;
  }

  return (
    <Box 
      sx={{ 
        maxWidth: '600px',
        '& .react-json-view': {
          backgroundColor: isDark ? 'rgba(15, 23, 42, 0.6)' : 'rgba(248, 250, 252, 0.8)',
          padding: '12px',
          borderRadius: '8px',
          fontSize: '0.8125rem',
          border: isDark 
            ? '1px solid rgba(148, 163, 184, 0.12)'
            : '1px solid rgba(226, 232, 240, 0.6)',
        }
      }}
    >
      <JsonViewer
        name="details"
        src={parsedDetail}
        collapsed={1}
        displayDataTypes={false}
        displayObjectSize={false}
        enableClipboard={true}
        style={{
          fontSize: '0.8125rem',
          lineHeight: 1.5,
        }}
      />
    </Box>
  );
};

const cellRenderer = {
  [HeadCellIds.ID]: ({ id }: FormattedRowData) => {
    return (
      <Box 
        sx={{ 
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          fontWeight: 500,
          color: 'primary.main'
        }}
      >
        {id}
      </Box>
    );
  },
  [HeadCellIds.CreateAt]: ({ createdAt }: FormattedRowData) => {
    return (
      <Box sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
        {falsyCheckDate(createdAt)}
      </Box>
    );
  },
  [HeadCellIds.EntityId]: ({ entityId }: FormattedRowData) => {
    return (
      <Box 
        sx={{ 
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          fontWeight: 500
        }}
      >
        {entityId}
      </Box>
    );
  },
  [HeadCellIds.ErrorDetail]: (data: FormattedRowData) => <ErrorDetailCell {...data} />,
  [HeadCellIds.ErrorSource]: ({ errorSource }: FormattedRowData) => {
    return (
      <Chip
        label={errorSource}
        size="small"
        sx={{
          fontFamily: 'monospace',
          fontSize: '0.75rem',
          fontWeight: 600,
          backgroundColor: (theme) => theme.palette.mode === 'dark'
            ? 'rgba(99, 102, 241, 0.15)'
            : 'rgba(99, 102, 241, 0.1)',
          color: (theme) => theme.palette.mode === 'dark'
            ? 'rgba(165, 180, 252, 1)'
            : 'rgba(67, 56, 202, 1)',
          border: (theme) => theme.palette.mode === 'dark'
            ? '1px solid rgba(99, 102, 241, 0.3)'
            : '1px solid rgba(99, 102, 241, 0.2)',
        }}
      />
    );
  },
  [HeadCellIds.ErrorType]: ({ errorType }: FormattedRowData) => {
    return (
      <Chip
        label={errorType}
        size="small"
        color="error"
        variant="outlined"
        sx={{
          fontFamily: 'monospace',
          fontSize: '0.75rem',
          fontWeight: 600,
        }}
      />
    );
  },
  [HeadCellIds.TargetServiceId]: ({ targetServiceId }: FormattedRowData) => {
    return (
      <Box 
        sx={{ 
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          fontWeight: 500,
          color: 'text.secondary'
        }}
      >
        {targetServiceId}
      </Box>
    );
  },
};

const tableConfigs = {
  default: {
    cellProps: {
      headCells: [
        {
          id: HeadCellIds.ID,
          label: "ID",
          style: { 
            minWidth: '80px',
            fontWeight: 600,
          }
        },
        {
          id: HeadCellIds.EntityId,
          label: "Entity ID",
          style: { 
            minWidth: '120px',
            fontWeight: 600,
          }
        },
        {
          id: HeadCellIds.TargetServiceId,
          label: "Target Service ID",
          style: { 
            minWidth: '140px',
            fontWeight: 600,
          }
        },
        {
          id: HeadCellIds.CreateAt,
          label: "Created At",
          style: { 
            minWidth: '180px',
            fontWeight: 600,
          }
        },
        {
          id: HeadCellIds.ErrorDetail,
          label: "Error Details",
          disableSort: true,
          style: { 
            minWidth: '400px',
            fontWeight: 600,
          }
        },
        {
          id: HeadCellIds.ErrorSource,
          label: "Error Source",
          style: { 
            minWidth: '140px',
            fontWeight: 600,
          }
        },
        {
          id: HeadCellIds.ErrorType,
          label: "Error Type",
          style: { 
            minWidth: '140px',
            fontWeight: 600,
          }
        },
      ],
    },
  },
  shared: {
    cellProps: {
      cellRenderer,
      headCells: [],
    },
    headerProps: {
      tableTitle: "Entity Errors",
    },
    paginationProps: {
      hidePagination: true,
    },
  },
};

export const getTableConfigs = ({
  variant = "default",
}: {
  variant?: string;
}) => {
  const tableConfig = cloneDeep(
    tableConfigs[variant as keyof typeof tableConfigs]
  );
  const sharedConfig = cloneDeep(tableConfigs.shared);

  return merge(sharedConfig, tableConfig);
};
